import React, { useState } from 'react';
import { Link, useNavigate, useLocation } from 'react-router-dom';
import styled from 'styled-components';
import { useAuth } from '../../contexts/AuthContext';
import { toast } from 'react-toastify';
import { FaBars, FaTimes } from 'react-icons/fa';

const LayoutContainer = styled.div`
  min-height: 100vh;
  display: flex;
  flex-direction: column;
`;

const Header = styled.header`
  background-color: #2c3e50;
  color: white;
  padding: 1rem 0;
  box-shadow: 0 2px 4px rgba(0, 0, 0, 0.1);
  position: relative;
  z-index: 1000;

  @media print {
    display: none;
  }
`;

const HeaderContent = styled.div`
  max-width: 1200px;
  margin: 0 auto;
  padding: 0 1rem;
  display: flex;
  justify-content: space-between;
  align-items: center;

  @media (min-width: 901px) {
    padding: 0 2rem;
  }
`;

const Logo = styled(Link)`
  font-size: 1.25rem;
  font-weight: bold;
  color: white;
  text-decoration: none;

  &:hover {
    color: #ecf0f1;
  }

  @media (min-width: 901px) {
    font-size: 1.5rem;
  }
`;

const HamburgerButton = styled.button`
  display: flex;
  align-items: center;
  justify-content: center;
  background: none;
  border: none;
  color: white;
  font-size: 1.5rem;
  cursor: pointer;
  padding: 0.5rem;
  z-index: 1001;

  @media (min-width: 901px) {
    display: none;
  }
`;

const Nav = styled.nav`
  display: none;

  @media (min-width: 901px) {
    display: flex;
    align-items: center;
    gap: 1rem;
  }
`;

const MobileNav = styled.nav`
  position: fixed;
  top: 0;
  left: 0;
  right: 0;
  bottom: 0;
  background-color: #2c3e50;
  display: flex;
  flex-direction: column;
  padding: 5rem 1.5rem 2rem;
  transform: translateX(${props => props.$isOpen ? '0' : '100%'});
  transition: transform 0.3s ease-in-out;
  z-index: 999;
  overflow-y: auto;

  @media (min-width: 901px) {
    display: none;
  }
`;

const MobileNavLink = styled(Link)`
  color: white;
  text-decoration: none;
  padding: 1rem;
  border-radius: 8px;
  font-size: 1.1rem;
  transition: background-color 0.3s;

  &:hover {
    background-color: rgba(255, 255, 255, 0.1);
  }

  &.active {
    background-color: rgba(255, 255, 255, 0.2);
  }
`;

const NavLink = styled(Link)`
  color: white;
  text-decoration: none;
  padding: 0.5rem 0.75rem;
  border-radius: 4px;
  transition: background-color 0.3s;
  font-size: 0.9rem;
  white-space: nowrap;

  &:hover {
    background-color: rgba(255, 255, 255, 0.1);
  }

  &.active {
    background-color: rgba(255, 255, 255, 0.2);
  }

  @media (min-width: 1024px) {
    padding: 0.5rem 1rem;
    font-size: 1rem;
  }
`;

const UserInfo = styled.div`
  display: none;

  @media (min-width: 901px) {
    display: flex;
    align-items: center;
    gap: 1rem;
  }
`;

const MobileUserInfo = styled.div`
  margin-top: auto;
  padding-top: 1.5rem;
  border-top: 1px solid rgba(255, 255, 255, 0.2);
`;

const MobileUserName = styled.div`
  color: rgba(255, 255, 255, 0.8);
  font-size: 0.9rem;
  margin-bottom: 1rem;
  padding: 0 1rem;
`;

const UserName = styled.span`
  font-size: 0.85rem;
  max-width: 150px;
  overflow: hidden;
  text-overflow: ellipsis;
  white-space: nowrap;
`;

const LogoutButton = styled.button`
  background-color: #e74c3c;
  color: white;
  border: none;
  padding: 0.5rem 1rem;
  border-radius: 4px;
  cursor: pointer;
  font-size: 0.9rem;
  transition: background-color 0.3s;

  &:hover {
    background-color: #c0392b;
  }
`;

const MobileLogoutButton = styled(LogoutButton)`
  width: 100%;
  padding: 1rem;
  font-size: 1rem;
  margin-top: 0.5rem;
`;

const Main = styled.main`
  flex: 1;
  background-color: #f5f5f5;
  padding: 1rem 0;

  @media (min-width: 901px) {
    padding: 2rem 0;
  }
`;

const Footer = styled.footer`
  background-color: #34495e;
  color: white;
  text-align: center;
  padding: 6rem 0 0.75rem;
  font-size: 0.75rem;

  @media (min-width: 901px) {
    padding: 6rem 0 1rem;
    font-size: 0.9rem;
  }

  @media print {
    display: none;
  }
`;

const Overlay = styled.div`
  position: fixed;
  top: 0;
  left: 0;
  right: 0;
  bottom: 0;
  background-color: rgba(0, 0, 0, 0.5);
  opacity: ${props => props.$isOpen ? 1 : 0};
  visibility: ${props => props.$isOpen ? 'visible' : 'hidden'};
  transition: opacity 0.3s, visibility 0.3s;
  z-index: 998;

  @media (min-width: 901px) {
    display: none;
  }
`;

const Layout = ({ children }) => {
  const navigate = useNavigate();
  const location = useLocation();
  const { user, logout, isAdmin } = useAuth();
  const [mobileMenuOpen, setMobileMenuOpen] = useState(false);

  const handleLogout = () => {
    logout();
    toast.info('Sesión cerrada');
    setMobileMenuOpen(false);
    navigate('/login');
  };

  const isActive = (path) => {
    return location.pathname === path ? 'active' : '';
  };

  const handleNavClick = () => {
    setMobileMenuOpen(false);
  };

  const toggleMobileMenu = () => {
    setMobileMenuOpen(!mobileMenuOpen);
  };

  return (
    <LayoutContainer>
      <Header>
        <HeaderContent>
          <Logo to="/" onClick={handleNavClick}>Calculator</Logo>

          {/* Desktop Navigation */}
          <Nav>
            <NavLink to="/calculator" className={isActive('/calculator')}>
              Calculadora
            </NavLink>
            {isAdmin && (
              <>
                <NavLink to="/orders" className={isActive('/orders')}>
                  Pedidos
                </NavLink>
                <NavLink to="/stock-shortages" className={isActive('/stock-shortages')}>
                  Stock
                </NavLink>
                <NavLink to="/delivery-notes" className={isActive('/delivery-notes')}>
                  Albaranes
                </NavLink>
                <NavLink to="/clients" className={isActive('/clients')}>
                  Clientes
                </NavLink>
                <NavLink to="/products" className={isActive('/products')}>
                  Productos
                </NavLink>
                <NavLink to="/historico" className={isActive('/historico')}>
                  Histórico
                </NavLink>
                <NavLink to="/users" className={isActive('/users')}>
                  Usuarios
                </NavLink>
              </>
            )}
          </Nav>

          <UserInfo>
            <UserName>
              {user?.firstName} {user?.lastName}
            </UserName>
            <LogoutButton onClick={handleLogout}>
              Cerrar Sesión
            </LogoutButton>
          </UserInfo>

          {/* Mobile Hamburger Button */}
          <HamburgerButton onClick={toggleMobileMenu} aria-label="Menu">
            {mobileMenuOpen ? <FaTimes /> : <FaBars />}
          </HamburgerButton>
        </HeaderContent>
      </Header>

      {/* Mobile Navigation Overlay */}
      <Overlay $isOpen={mobileMenuOpen} onClick={() => setMobileMenuOpen(false)} />

      {/* Mobile Navigation Menu */}
      <MobileNav $isOpen={mobileMenuOpen}>
        <MobileNavLink to="/calculator" className={isActive('/calculator')} onClick={handleNavClick}>
          Calculadora
        </MobileNavLink>
        {isAdmin && (
          <>
            <MobileNavLink to="/orders" className={isActive('/orders')} onClick={handleNavClick}>
              Pedidos
            </MobileNavLink>
            <MobileNavLink to="/stock-shortages" className={isActive('/stock-shortages')} onClick={handleNavClick}>
              Faltas de Stock
            </MobileNavLink>
            <MobileNavLink to="/delivery-notes" className={isActive('/delivery-notes')} onClick={handleNavClick}>
              Albaranes
            </MobileNavLink>
            <MobileNavLink to="/clients" className={isActive('/clients')} onClick={handleNavClick}>
              Clientes
            </MobileNavLink>
            <MobileNavLink to="/products" className={isActive('/products')} onClick={handleNavClick}>
              Productos
            </MobileNavLink>
            <MobileNavLink to="/historico" className={isActive('/historico')} onClick={handleNavClick}>
              Histórico
            </MobileNavLink>
            <MobileNavLink to="/users" className={isActive('/users')} onClick={handleNavClick}>
              Usuarios
            </MobileNavLink>
          </>
        )}

        <MobileUserInfo>
          <MobileUserName>
            {user?.firstName} {user?.lastName}
            {user?.company && ` - ${user.company}`}
          </MobileUserName>
          <MobileLogoutButton onClick={handleLogout}>
            Cerrar Sesión
          </MobileLogoutButton>
        </MobileUserInfo>
      </MobileNav>

      <Main>{children}</Main>

      <Footer>
        <p>&copy; 2025 ILTZAR S.L.U. - Sistema de Cálculo de Marcos v2.0</p>
      </Footer>
    </LayoutContainer>
  );
};

export default Layout;